import React, { useState, useEffect, useRef } from 'react';
import './PriceRangeSlider.css';
import { formatPrice } from '../utils/shopifyDataTransformers';

interface PriceRangeSliderProps {
  min: number;
  max: number;
  step: number;
  minValue: number;
  maxValue: number;
  onChange: (minValue: number, maxValue: number) => void;
}

const PriceRangeSlider: React.FC<PriceRangeSliderProps> = ({
  min,
  max,
  step,
  minValue,
  maxValue,
  onChange
}) => {
  const [localMinValue, setLocalMinValue] = useState<number>(minValue);
  const [localMaxValue, setLocalMaxValue] = useState<number>(maxValue);
  const rangeRef = useRef<HTMLDivElement>(null);

  // Update local state when props change
  useEffect(() => {
    setLocalMinValue(minValue);
    setLocalMaxValue(maxValue);
  }, [minValue, maxValue]);

  // Calculate percentage for styling
  const getPercent = (value: number): number => {
    return Math.round(((value - min) / (max - min)) * 100);
  };

  // Update the range progress bar
  useEffect(() => {
    if (rangeRef.current) {
      const minPercent = getPercent(localMinValue);
      const maxPercent = getPercent(localMaxValue);
      
      rangeRef.current.style.left = `${minPercent}%`;
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [localMinValue, localMaxValue, min, max]);

  // Handlers for slider changes
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(e.target.value), localMaxValue - step);
    setLocalMinValue(value);
    onChange(value, localMaxValue);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(Number(e.target.value), localMinValue + step);
    setLocalMaxValue(value);
    onChange(localMinValue, value);
  };

  return (
    <div className="price-range-slider">
      <div className="slider-container">
        <div className="slider-track" data-content="track">&nbsp;</div>
        <div ref={rangeRef} className="slider-range" data-content="range">&nbsp;</div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localMinValue}
          onChange={handleMinChange}
          className="thumb thumb--left"
          style={{ zIndex: localMinValue > max - 100 ? 5 : undefined }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localMaxValue}
          onChange={handleMaxChange}
          className="thumb thumb--right"
        />
      </div>
      
      <div className="slider-values">
        <div className="min-value">
          <span className="value-label">Min:</span>
          <span className="value-amount">${formatPrice(localMinValue)}</span>
        </div>
        <div className="max-value">
          <span className="value-label">Max:</span>
          <span className="value-amount">${formatPrice(localMaxValue)}</span>
        </div>
      </div>
    </div>
  );
};

export default PriceRangeSlider;